/**
 * Analytics event catalog.
 *
 * Exports:
 *   - EVENT_TYPES — canonical event_type string constants
 *   - AnalyticsEventType — discriminated union of all typed analytics events
 *
 * Adding a new event:
 *   1. Define a *Data interface in the appropriate domain file.
 *   2. Add an entry to EVENT_TYPES.
 *   3. Add AnalyticsEvent<YourNewData> to the AnalyticsEventType union.
 */
import type { AnalyticsEvent } from '../types';

import type {
  ProductViewedData,
  ProductAddedToCartData,
} from './domains/product-events';

import type {
  CheckoutStartedData,
  PaymentCompletedData,
  OrderPlacedData,
} from './domains/checkout-events';

import type {
  FunnelEvaluatedData,
  OfferAcceptedData,
  OfferDeclinedData,
} from './domains/funnel-events';

import type {
  PageViewedData,
  SearchPerformedData,
} from './domains/navigation-events';

// ---------------------------------------------------------------------------
// Canonical event type string constants
// ---------------------------------------------------------------------------

/**
 * Immutable map of all event_type identifiers.
 * Use these constants everywhere — never hard-code the strings at call sites.
 */
export const EVENT_TYPES = {
  // product domain
  PRODUCT_VIEWED: 'product.viewed.v1',
  PRODUCT_ADDED_TO_CART: 'product.added_to_cart.v1',
  // checkout domain
  CHECKOUT_STARTED: 'checkout.started.v1',
  PAYMENT_COMPLETED: 'checkout.payment_completed.v1',
  ORDER_PLACED: 'checkout.order_placed.v1',
  // funnel domain
  FUNNEL_EVALUATED: 'funnel.evaluated.v1',
  OFFER_ACCEPTED: 'funnel.offer_accepted.v1',
  OFFER_DECLINED: 'funnel.offer_declined.v1',
  // navigation domain
  PAGE_VIEWED: 'navigation.page_viewed.v1',
  SEARCH_PERFORMED: 'navigation.search_performed.v1',
} as const;

/** Union of all registered event_type string literals */
export type EventTypeLiteral = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

/**
 * Canonical facts that may originate in the storefront browser pipeline.
 *
 * Keep authoritative payment/order facts out of this list: those are emitted
 * only by their owning services after the durable state transition succeeds.
 */
export const BROWSER_EVENT_TYPES = [
  EVENT_TYPES.PAGE_VIEWED,
  EVENT_TYPES.SEARCH_PERFORMED,
  EVENT_TYPES.PRODUCT_VIEWED,
  EVENT_TYPES.PRODUCT_ADDED_TO_CART,
  EVENT_TYPES.CHECKOUT_STARTED,
  EVENT_TYPES.FUNNEL_EVALUATED,
  EVENT_TYPES.OFFER_ACCEPTED,
  EVENT_TYPES.OFFER_DECLINED,
] as const satisfies readonly EventTypeLiteral[];

export type BrowserEventTypeLiteral = (typeof BROWSER_EVENT_TYPES)[number];

/** Canonical facts owned exclusively by backend domain services. */
export const SERVER_EVENT_TYPES = [
  EVENT_TYPES.PAYMENT_COMPLETED,
  EVENT_TYPES.ORDER_PLACED,
] as const satisfies readonly EventTypeLiteral[];

export type ServerEventTypeLiteral = (typeof SERVER_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Discriminated union of all analytics events
// ---------------------------------------------------------------------------

/**
 * Discriminated union.  TypeScript narrows on `metadata.event_type` when you
 * pattern-match against an EVENT_TYPES constant.
 */
export type AnalyticsEventType =
  | AnalyticsEvent<ProductViewedData>
  | AnalyticsEvent<ProductAddedToCartData>
  | AnalyticsEvent<CheckoutStartedData>
  | AnalyticsEvent<PaymentCompletedData>
  | AnalyticsEvent<OrderPlacedData>
  | AnalyticsEvent<FunnelEvaluatedData>
  | AnalyticsEvent<OfferAcceptedData>
  | AnalyticsEvent<OfferDeclinedData>
  | AnalyticsEvent<PageViewedData>
  | AnalyticsEvent<SearchPerformedData>;
